import { cn } from "@/modern/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import type { ClockingCell } from "../types";
import {
  ATTENDANCE_WORKFLOW_STATUS,
  getStatusColor,
  TIME_OFF_APPROVAL,
  TIME_OFF_APPROVAL_LABEL,
} from "../status/displayStatusColors";

type DayCellContentProps = {
  cell: ClockingCell;
  selected?: boolean;
  className?: string;
};

function sameLabel(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.replace(/[\s_-]+/g, "").toLowerCase() === b.replace(/[\s_-]+/g, "").toLowerCase();
}

function workflowOutcomeLabel(cell: ClockingCell): string | null {
  // Prefer API label; fall back to status codes when label is missing.
  if (cell.statusLabel) return cell.statusLabel;
  const code = Number(cell.status);
  if (code === ATTENDANCE_WORKFLOW_STATUS.REVIEWED) return "Reviewed";
  if (code === ATTENDANCE_WORKFLOW_STATUS.EXCUSED) return "Excused";
  if (code === ATTENDANCE_WORKFLOW_STATUS.CORRECTED) return "Corrected";
  return null;
}

/**
 * Minimal calendar event: color from display_status; show API status_label when it differs.
 */
export function DayCellContent({ cell, selected, className }: DayCellContentProps) {
  const hasPunch = cell.attendanceId != null;
  const hasTimeOff = cell.timeOffId != null;
  const isEmpty = !hasPunch && !hasTimeOff && !cell.displayStatus;

  if (isEmpty) {
    return <div className={cn("h-12", className)} />;
  }

  const status = getStatusColor(cell.displayStatus);
  const pendingTimeOff = cell.timeOffApprovalStatus === TIME_OFF_APPROVAL.PENDING;
  const timeOffOnly = hasTimeOff && !hasPunch;
  const layered = hasTimeOff && hasPunch;

  const label = timeOffOnly
    ? pendingTimeOff
      ? "Time off · Pending"
      : "Time off"
    : cell.displayStatusLabel || status.label;

  const outcome = workflowOutcomeLabel(cell);
  const statusLabel =
    !timeOffOnly && outcome && !sameLabel(outcome, label) ? outcome : null;
  const timeLine = timeOffOnly ? null : formatPunchLine(cell.clockInAt, cell.clockOutAt);
  const secondaryLine = statusLabel ?? timeLine;

  const tooltipLines = [
    cell.displayStatusLabel || status.label,
    statusLabel,
    layered ? "Overlaps time off" : null,
    hasTimeOff && cell.timeOffApprovalStatus
      ? `Time off · ${TIME_OFF_APPROVAL_LABEL[cell.timeOffApprovalStatus]}`
      : null,
    cell.clockInAt ? `In  ${formatTime(cell.clockInAt)}` : null,
    cell.clockOutAt ? `Out ${formatTime(cell.clockOutAt)}` : null,
  ].filter(Boolean) as string[];

  const card = (
    <div
      className={cn(
        "relative flex h-12 flex-col justify-center gap-0.5 overflow-hidden rounded-md border px-2.5 py-1.5",
        status.bg,
        selected
          ? "border-2 border-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.35)]"
          : status.border,
        className
      )}
    >
      <span
        className={cn(
          "absolute inset-y-0 left-0",
          selected ? "w-1.5" : "w-[3px]",
          status.accent
        )}
      />

      <span className={cn("truncate pl-1.5 text-xs font-medium leading-tight", status.text)}>
        {label}
      </span>
      {secondaryLine ? (
        <span
          className={cn(
            "truncate pl-1.5 text-[11px] leading-tight",
            statusLabel ? "font-medium text-foreground" : cn("tabular-nums", status.mutedText)
          )}
        >
          {secondaryLine}
        </span>
      ) : null}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="w-full">{card}</div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[220px] border text-left">
        <div className="space-y-0.5">
          {tooltipLines.map((line) => (
            <p key={line} className="text-xs">
              {line}
            </p>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatPunchLine(clockIn: string | null, clockOut: string | null): string | null {
  if (clockIn && clockOut) return `${formatTime(clockIn)} – ${formatTime(clockOut)}`;
  if (clockIn) return `${formatTime(clockIn)} →`;
  if (clockOut) return `→ ${formatTime(clockOut)}`;
  return null;
}
